import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {isObject} from "lodash";
import {toast} from "react-toastify";
import {
  STATUS_PRINTED as STATUS_PRINTED_webSocket
} from "../components/SaleDocument/PrintButton/ReceiptFiscalPrintButtonWebSocket";
import {useTranslation} from "react-i18next";

export const ACTION_NOTIFICATIONS_UPDATE_COUNT = 'notificationsUpdateCount';
export const ACTION_RECEIPT_FISCAL_PRINT = 'receiptFiscalPrint';
export const ACTION_RECEIPT_FISCAL_NOT_FOUND = 'receiptFiscalNotFound';
export const ACTION_RECEIPT_FISCAL_PRINTING = 'receiptFiscalPrinting';
export const ACTION_RECEIPT_FISCAL_PRINTED_SUCCESS = 'receiptFiscalPrintedSuccess';
export const ACTION_RECEIPT_FISCAL_PRINTED_ERROR = 'receiptFiscalPrintedError';
export const ACTION_RECEIPT_FISCAL_PRINTER_NOT_FOUND = 'receiptFiscalPrinterNotFound';
export const ACTION_RECEIPT_FISCAL_PRINTER_NOT_CONNECTED = 'receiptFiscalPrinterNotConnected';
export const ACTION_RECEIPT_FISCAL_PRINTER_CONNECTION_FAILED = 'receiptFiscalPrinterConnectionFailed';

const WebSocketContext = createContext(null);

const getSaleDocumentBsxPrinterListeners = (addListener, t) => {
  return [
    addListener(ACTION_RECEIPT_FISCAL_PRINTING, ({number}) => {
      toast.info(t('receipt_fiscal_print_button:printing_started', {number: number}))
    }),
    addListener(ACTION_RECEIPT_FISCAL_PRINTED_SUCCESS, ({bsxStatus, bsxPrinterStatus, number}) => {
      if(bsxStatus){
        if(bsxStatus.toString() === STATUS_PRINTED_webSocket) {
          toast.success(t('receipt_fiscal_print_button:printed_success', {number: number}))
        } else {
          toast.error(t('receipt_fiscal_print_button:printed_error', {number: number}));
        }
      }
      if(bsxPrinterStatus){
        toast.success(t('receipt_fiscal_print_button:printed_success', {number: number}))
      }
    }),
    addListener(ACTION_RECEIPT_FISCAL_PRINTED_ERROR, ({bsxStatus, bsxPrinterStatus, number}) => {
      if(bsxStatus){
        if(bsxStatus.toString() === STATUS_PRINTED_webSocket) {
          toast.success(t('receipt_fiscal_print_button:printed_success', {number: number}))
        } else {
          toast.error(t('receipt_fiscal_print_button:printed_error', {number: number}));
        }
      }
      if(bsxPrinterStatus){
        toast.error(t('receipt_fiscal_print_button:printed_error', {number: number}));
      }
    }),
    addListener(ACTION_RECEIPT_FISCAL_PRINTER_NOT_FOUND, () => {
      toast.warn(t('receipt_fiscal_print_button:receipt_fiscal_printer_not_found'))
    }),
    addListener(ACTION_RECEIPT_FISCAL_PRINTER_NOT_CONNECTED, () => {
      toast.warn(t('receipt_fiscal_print_button:receipt_fiscal_printer_not_connected'))
    }),
    addListener(ACTION_RECEIPT_FISCAL_PRINTER_CONNECTION_FAILED, () => {
      toast.warn(t('receipt_fiscal_print_button:receipt_fiscal_printer_connection_failed'))
    }),
    addListener(ACTION_RECEIPT_FISCAL_NOT_FOUND, () => {
      toast.warn(t('receipt_fiscal_print_button:receipt_fiscal_not_found'))
    }),
  ]
}

export const WebSocketProvider = ({ children, reconnectInterval = 1000 * 60 }) => {
  const { t } = useTranslation()
  const url = process.env.REACT_APP_WEBSOCKET_DEFAULT_URL;
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [messages, setMessages] = useState([]);
  const listeners = useRef(new Map())

  useEffect(() => {
    const saleDocumentBsxPrinterListenerActions = getSaleDocumentBsxPrinterListeners(addListener, t);

    return () => {
      [
        ...saleDocumentBsxPrinterListenerActions
      ].forEach(removeListener)
    }
  });

  const addListener = (action, callback) => {
    listeners.current.set(action, callback);

    return action
  };

  const removeListener = (action) => {
    listeners.current.delete(action);
  };

  const sendMessage = (message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(isObject(message) ? JSON.stringify(message) : message);
    }
  };

  const connectWebSocket = () => {
    const ws = new WebSocket(url + `?jwt=${localStorage.getItem('jwt')}`);

    ws.onopen = () => {
      setIsConnected(true);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const action = data.action;

      if (listeners.current.has(action)) {
        listeners.current.get(action)(data);
      } else {
        setMessages((prev) => [...prev, data]);
      }
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      setIsConnected(false);
      attemptReconnect();
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socketRef.current = ws;
  };

  const attemptReconnect = () => {
    setTimeout(() => {
      connectWebSocket();
    }, reconnectInterval)
  };

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [url, reconnectInterval]);

  return (
    <WebSocketContext.Provider value={{ sendMessage, addListener, removeListener, messages, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
